import { ref, type Ref } from 'vue'

export type AnchorPlacement =
  | 'bottom-start'
  | 'bottom-end'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'top'
  | 'right-start'
  | 'right-end'
  | 'right'
  | 'left-start'
  | 'left-end'
  | 'left'

export interface AnchoredPositionConfig {
  placement?: AnchorPlacement
  /** Gap between the trigger and the panel, in px. */
  offset?: number
  /** Force the panel to the trigger's width (menus / selects). */
  matchWidth?: boolean
  /** Minimum gap kept between the panel and every viewport edge, in px. */
  padding?: number
}

export interface UseAnchoredPosition {
  /** Reactive `position: fixed` style bag to bind on the floating element; its `transformOrigin` is the edge facing the trigger. */
  floatingStyles: Ref<Record<string, string>>
  update: () => void
  /** Measure once and begin tracking scroll / resize / size changes. */
  start: () => void
  stop: () => void
}

const ANCHOR_SEARCH_DEPTH = 4

function firstBoxedRect(el: Element, depth: number): DOMRect | null {
  const rect = el.getBoundingClientRect()
  if (rect.width > 0 || rect.height > 0) return rect
  if (depth === 0) return null
  for (const child of el.children) {
    const found = firstBoxedRect(child, depth - 1)
    if (found) return found
  }
  return null
}

/** Hazard: `AppTooltip` and `AppPopover` anchor to a `display: contents` span with no box, so the real trigger is found below it, through nested spans and `display: none` breakpoint halves. */
function anchorRect(el: HTMLElement): DOMRect {
  return firstBoxedRect(el, ANCHOR_SEARCH_DEPTH) ?? el.getBoundingClientRect()
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min
  return Math.min(Math.max(value, min), max)
}

export function useAnchoredPosition(
  anchorRef: Ref<HTMLElement | null>,
  floatingRef: Ref<HTMLElement | null>,
  config: () => AnchoredPositionConfig,
): UseAnchoredPosition {
  const floatingStyles = ref<Record<string, string>>({ position: 'fixed', top: '0px', left: '0px' })

  let rafId = 0
  let resizeObserver: ResizeObserver | undefined

  function update(): void {
    const anchor = anchorRef.value
    const floating = floatingRef.value
    if (!anchor || !floating || typeof window === 'undefined') return

    const { placement = 'bottom-start', offset = 8, matchWidth = false, padding = 8 } = config()

    const a = anchorRect(anchor)
    const f = floating.getBoundingClientRect()
    const viewportWidth = document.documentElement.clientWidth
    const viewportHeight = document.documentElement.clientHeight

    const width = matchWidth ? a.width : f.width
    const height = f.height

    const horizontal = placement.startsWith('left') || placement.startsWith('right')
    const align = placement.endsWith('-start')
      ? 'start'
      : placement.endsWith('-end')
        ? 'end'
        : 'center'

    let top: number
    let left: number
    let origin: 'top' | 'bottom' | 'left' | 'right'

    if (horizontal) {
      const preferredSide = placement.startsWith('left') ? 'left' : 'right'
      const spaceRight = viewportWidth - a.right
      const spaceLeft = a.left
      const needed = width + offset + padding

      let side = preferredSide
      if (preferredSide === 'right' && spaceRight < needed && spaceLeft > spaceRight) side = 'left'
      if (preferredSide === 'left' && spaceLeft < needed && spaceRight > spaceLeft) side = 'right'

      left = side === 'right' ? a.right + offset : a.left - width - offset
      origin = side === 'right' ? 'left' : 'right'

      if (align === 'start') top = a.top
      else if (align === 'end') top = a.bottom - height
      else top = a.top + a.height / 2 - height / 2
    } else {
      const preferredSide = placement.startsWith('top') ? 'top' : 'bottom'
      const spaceBelow = viewportHeight - a.bottom
      const spaceAbove = a.top
      const needed = height + offset + padding

      let side = preferredSide
      if (preferredSide === 'bottom' && spaceBelow < needed && spaceAbove > spaceBelow) side = 'top'
      if (preferredSide === 'top' && spaceAbove < needed && spaceBelow > spaceAbove) side = 'bottom'

      top = side === 'bottom' ? a.bottom + offset : a.top - height - offset
      origin = side === 'bottom' ? 'top' : 'bottom'

      if (align === 'start') left = a.left
      else if (align === 'end') left = a.right - width
      else left = a.left + a.width / 2 - width / 2
    }

    const clampedLeft = clamp(left, padding, viewportWidth - width - padding)
    const clampedTop = clamp(top, padding, viewportHeight - height - padding)

    const maxHeight = Math.max(0, viewportHeight - clampedTop - padding)

    floatingStyles.value = {
      position: 'fixed',
      top: `${Math.round(clampedTop)}px`,
      left: `${Math.round(clampedLeft)}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
      transformOrigin: origin,
      ...(matchWidth ? { width: `${Math.round(width)}px` } : {}),
    }
  }

  function scheduleUpdate(): void {
    if (typeof window === 'undefined') return
    cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(update)
  }

  function start(): void {
    if (typeof window === 'undefined') return
    update()
    window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true })
    window.addEventListener('resize', scheduleUpdate, { passive: true })
    if (floatingRef.value && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate)
      resizeObserver.observe(floatingRef.value)
    }
  }

  function stop(): void {
    if (typeof window === 'undefined') return
    cancelAnimationFrame(rafId)
    window.removeEventListener('scroll', scheduleUpdate, { capture: true })
    window.removeEventListener('resize', scheduleUpdate)
    resizeObserver?.disconnect()
    resizeObserver = undefined
  }

  return { floatingStyles, update, start, stop }
}
